export type ChatUI = {
  id: string;
  name: string;
  avatar?: string | null;

  time: string;
  lastMessage: {
    text: string;
    sender?: string | null;
    type?: string;
    ogType?: string;
    /** Ack da última mensagem, só preenchido quando fui eu que enviei. */
    status?: ChatReadStatus | null;
    isFromMe?: boolean;
  };

  unreadCount: number;

  isPinned: boolean;
  isMuted: boolean;
  isFavorite: boolean;

  tags: ResponseEtiqueta[];
  atendentes: { idUsuario: IdUsuario }[];

  idInbox?: IdInbox;
  status?: string;
  otherUserId?: string;
  isOnline?: boolean;
  estados?: {
    idUsuario: IdUsuario;
    ultimaLeitura: string | null;
    ultimaEntrega?: string | null;
  }[];
};

import { defineStore } from "pinia";
import {
  ChatsInternosService,
  ChatInternoListItem,
  MensagemQueryParams,
  MensagemInterna,
  UsuarioConta,
  ChatInternoSocketPayload,
  ChatInterno,
} from "../../services/chat-interno/internal-chat.service";
import { useAuthStoreUser } from "./authUser";
import { ChatReadStatus, Message } from "../../mocks/app/mockChatData";
import {
  WebhookPayload,
  WebsocketService,
} from "../../services/websocket/websocket.service";
import { StorageService } from "../admin/storage";
import { getDisplayTextForMessageContent } from "@/services/mensagens/mensagens.service";
import { IdInbox, IdUsuario, makeIdMensagem } from "@/services/common-types";
import { ResponseEtiqueta } from "@/services/etiquetas/etiquetas.service";
import { toast } from "vue3-toastify";
import { resolveSelectableMessageIds } from "@/utils/resolveSelectableMessageIds";
import {
  legendaParaEncaminhar,
  OpcoesEncaminhamento,
} from "@/utils/legendaEncaminhamento";
import { reactive } from "vue";
import type { PresencaChatInterno } from "../../services/websocket/websocket.service";

const chatsInternosService = new ChatsInternosService();

/**
 * Quem está digitando, por chat interno. Chat interno é sempre 1:1, então cabe UM
 * digitador por chat — ao contrário do WhatsApp, onde um grupo tem vários.
 *
 * Fora do `state` do Pinia de propósito, como `presencaChatState` no `chatStore`:
 * este é um option store, e um getter que chama action fecha um ciclo de inferência
 * que degrada o `this` do store INTEIRO. Funções de módulo não têm esse risco.
 */
const presencaChatInternoState = reactive({
    porChat: new Map<string, { idUsuario: string; presenca: PresencaChatInterno }>(),
});

/** Timers de expiração: detalhe de implementação, não estado de UI. */
const presencaChatInternoTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Sem `paused` o indicador ficaria preso: a aba pode fechar, a rede cair, o socket
 * perder o frame. Mesmo valor do WhatsApp — mas aqui o motivo é mais forte, porque
 * o emissor é um NAVEGADOR, que some sem avisar ninguém.
 */
const PRESENCA_CHAT_INTERNO_TTL_MS = 8000;

/**
 * Intervalo mínimo entre dois `composing` do MESMO chat.
 *
 * Aqui quem gera volume é o teclado do operador, não um gateway: sem isto, cada
 * tecla vira um frame de socket. No caminho do WhatsApp o dedupe mora no servidor
 * porque o ritmo vinha de fora; neste, a origem é local e o corte tem de ser local.
 * 3 s espelha `PRESENCA_DEDUPE_SEGUNDOS` do whiz-server, e é bem abaixo do TTL de
 * 8 s — o indicador do outro lado é reafirmado antes de expirar.
 */
const PRESENCA_CHAT_INTERNO_THROTTLE_MS = 3000;

/** Último `composing` enviado por chat, para o throttle acima. */
const ultimoEnvioDigitando = new Map<string, number>();

export function aplicarPresencaChatInterno(payload: {
    idChat: string;
    idUsuario: string;
    presenca: PresencaChatInterno;
}) {
    const { idChat, idUsuario, presenca } = payload;
    if (!idChat || !idUsuario) return;

    const timerAnterior = presencaChatInternoTimers.get(idChat);
    if (timerAnterior) {
        clearTimeout(timerAnterior);
        presencaChatInternoTimers.delete(idChat);
    }

    if (presenca === "paused") {
        presencaChatInternoState.porChat.delete(idChat);
        return;
    }

    presencaChatInternoState.porChat.set(idChat, { idUsuario, presenca });
    presencaChatInternoTimers.set(
        idChat,
        setTimeout(() => limparPresencaChatInterno(idChat), PRESENCA_CHAT_INTERNO_TTL_MS),
    );
}

export function limparPresencaChatInterno(idChat: string) {
    const timer = presencaChatInternoTimers.get(idChat);
    if (timer) {
        clearTimeout(timer);
        presencaChatInternoTimers.delete(idChat);
    }
    presencaChatInternoState.porChat.delete(idChat);
}

export function limparTodasPresencasChatInterno() {
    for (const timer of presencaChatInternoTimers.values()) clearTimeout(timer);
    presencaChatInternoTimers.clear();
    presencaChatInternoState.porChat.clear();
    ultimoEnvioDigitando.clear();
}

/**
 * Rótulo pronto, ou `null` quando ninguém digita. Sem nome: chat interno é 1:1 e
 * quem digita só pode ser a pessoa do outro lado — nomeá-la seria ruído, mesma
 * decisão do 1:1 do WhatsApp.
 */
export function rotuloPresencaChatInterno(idChat: string | null | undefined): string | null {
    if (!idChat) return null;
    return presencaChatInternoState.porChat.has(idChat) ? "escrevendo..." : null;
}

/**
 * Anuncia que ESTE operador está digitando. Throttled: no máximo um frame a cada
 * `PRESENCA_CHAT_INTERNO_THROTTLE_MS` por chat.
 */
export function notificarDigitacaoChatInterno(
    ws: WebsocketService,
    idChat: string | null | undefined,
    idDestinatario: string | null | undefined,
) {
    if (!idChat || !idDestinatario) return;

    const agora = Date.now();
    const ultimo = ultimoEnvioDigitando.get(idChat) ?? 0;
    if (agora - ultimo < PRESENCA_CHAT_INTERNO_THROTTLE_MS) return;

    ultimoEnvioDigitando.set(idChat, agora);
    ws.emitDigitandoChatInterno({ idChat, idDestinatario, presenca: "composing" });
}

/**
 * Anuncia que parou. Sempre enviado, sem throttle: é o sinal que APAGA o indicador
 * do outro lado, e engoli-lo deixaria a bolha acesa até o TTL. Também zera o
 * throttle, para a próxima digitação anunciar na hora em vez de esperar a janela.
 */
export function notificarParouDeDigitarChatInterno(
    ws: WebsocketService,
    idChat: string | null | undefined,
    idDestinatario: string | null | undefined,
) {
    if (!idChat || !idDestinatario) return;
    if (!ultimoEnvioDigitando.has(idChat)) return;

    ultimoEnvioDigitando.delete(idChat);
    ws.emitDigitandoChatInterno({ idChat, idDestinatario, presenca: "paused" });
}
const storageService = new StorageService();

/**
 * Ack da última mensagem para a sidebar. Chat interno é 1:1, então o estado que
 * importa é o do OUTRO usuário: `ultimaLeitura`/`ultimaEntrega` dele comparados
 * com a data da última mensagem — mesma regra que `loadMessages` aplica mensagem
 * por mensagem, só que aqui para a prévia.
 *
 * Retorna `null` quando a última mensagem não é minha: ack de mensagem recebida
 * não se mostra (é o outro lado que confirma, não eu).
 */
function ackUltimaMensagemInterna(params: {
  enviadaPorMim: boolean;
  dataUltimaMensagem?: string | null;
  estados?: ChatUI["estados"];
  meuId?: string | null;
}): ChatReadStatus | null {
  const { enviadaPorMim, dataUltimaMensagem, estados, meuId } = params;
  if (!enviadaPorMim) return null;
  if (!dataUltimaMensagem) return "sent";

  const dataEnvio = new Date(dataUltimaMensagem);
  if (isNaN(dataEnvio.getTime())) return "sent";

  const estadoOutro = estados?.find((e) => String(e.idUsuario) !== String(meuId));
  if (!estadoOutro) return "sent";

  const leitura = estadoOutro.ultimaLeitura
    ? new Date(estadoOutro.ultimaLeitura)
    : null;
  if (leitura && !isNaN(leitura.getTime()) && dataEnvio <= leitura) return "read";

  const entrega = estadoOutro.ultimaEntrega
    ? new Date(estadoOutro.ultimaEntrega)
    : null;
  if (entrega && !isNaN(entrega.getTime()) && dataEnvio <= entrega)
    return "delivered";

  return "sent";
}

export type InternalChatStore = ReturnType<typeof useInternalChatStore>;
export const useInternalChatStore = defineStore("internalChat", {
  state: () => ({
    contacts: [] as ChatUI[],
    activeChatId: null as string | null,
    messages: [] as Message[],
    messageToEdit: null as Message | null,
    messageToReply: null as Message | null,

    // UI
    isLoadingChats: false,
    isLoadingMessages: false,
    nextCursor: undefined as string | undefined,
    initialized: false, // Inicia em false
    wsInitialized: false, // Flag para callbacks de WS
    isLoadingContacts: false,
    draftsDict: {} as Record<string, string>,
    isInternalChatMode: false,
    processedMessageIds: new Set<string>(),

    // Notificações
    chatsComMensagensNavas: new Set<string>(), // IDs dos chats com novas mensagens
    audioNotificationEnabled: true, // Controlar som
    unreadCountByChat: {} as Record<string, number>, // Contar mensa

    // Forwarding State
    isForwarding: false,
    forwardingMessageIds: [] as string[],
    selectionContext: null as "manage" | "forward" | null,

    // Serviço WebSocket
    wsService: null as WebsocketService | null,
    initialUnreadCount: 0 as number,
    activeChatDraft: null as ChatUI | null,
    // 🔵 Todos os chats (com ou sem mensagens) - backend sync
    allChats: [] as ChatUI[],

    // 🟢 Chats visiveis (com mensagens) - o que aparece na sidebar
    visibleChats: [] as ChatUI[],
  }),

  getters: {
    // ⚠️ KEEP para compatibilidade (é alias de visibleChats)
    chats(state): ChatUI[] {
      return state.visibleChats;
    },

    getChatById: (state) => {
      return (chatId: string): ChatUI | null => {
        // 1️⃣ Procura em allChats primeiro
        const chatEmTodos = state.allChats.find((c) => c.id === chatId);
        if (chatEmTodos) return chatEmTodos;

        // 2️⃣ Se não achou, procura em draft
        if (state.activeChatDraft?.id === chatId) {
          return state.activeChatDraft;
        }

        return null;
      };
    },

    activeChat: (state) => {
      // 1️⃣ Tentar pegar de visibleChats (com mensagens)
      const chatComMensagens = state.visibleChats.find(
        (c) => c.id === state.activeChatId,
      );
      if (chatComMensagens) return chatComMensagens;

      // 2️⃣ Se não achou, procura em allChats (sem mensagens ainda)
      const chatTodos = state.allChats.find((c) => c.id === state.activeChatId);
      if (chatTodos) return chatTodos;

      // 3️⃣ Último recurso: draft
      return state.activeChatDraft || null;
    },
    messagesById(state): Map<string, Message> {
      return new Map(state.messages.map((m) => [m.id, m]));
    },

    /**
     * Conta a quantidade TOTAL de chats existentes
     * Usado para o badge ao lado de "Chats"
     */
    totalChatsCount(): number {
      if (!this.isInternalChatMode) return 0;
      return this.chats.length;
    },

    /**
     * Conta a quantidade TOTAL de contatos existentes
     * Usado para o badge ao lado de "Contatos"
     */
    totalContactsCount(): number {
      if (!this.isInternalChatMode) return 0;
      return this.contacts.length;
    },
    /**
     * Verifica se há alguma mensagem não lida
     */
    hasUnreadMessages(): boolean {
      return this.totalUnreadCount > 0;
    },

    /**
     * Retorna lista de chats com mensagens não lidas
     */
    chatsWithUnread(): ChatUI[] {
      return this.chats.filter((chat) => this.unreadCountByChat[chat.id] > 0);
    },
    totalUnreadCount(): number {
      return Object.values(this.unreadCountByChat).reduce((a, b) => a + b, 0);
    },
  },

  actions: {
    closeChat() {
        this.activeChatId = null;
        this.messages = [];
    },

    incrementUnreadCount(chatId: string) {
      if (!this.unreadCountByChat[chatId]) {
        this.unreadCountByChat[chatId] = 0;
      }
      this.unreadCountByChat[chatId]++;
      this.chatsComMensagensNavas.add(chatId);
    },

    markChatAsRead(chatId: string) {
      if (this.unreadCountByChat[chatId]) {
        this.unreadCountByChat[chatId] = 0;
      }
      this.chatsComMensagensNavas.delete(chatId);
    },

    setAudioNotification(enabled: boolean) {
      this.audioNotificationEnabled = enabled;
    },

    /**
     * Marca um id de mensagem como já processado, mantendo o Set limitado.
     * Sessões longas acumulariam milhares de ids; limitamos a um teto FIFO simples.
     */
    _markProcessed(id: string) {
      const MAX = 500;
      if (this.processedMessageIds.size >= MAX) {
        const oldest = this.processedMessageIds.values().next().value;
        if (oldest !== undefined) this.processedMessageIds.delete(oldest);
      }
      this.processedMessageIds.add(id);
    },

    playNotificationSound() {
      if (!this.audioNotificationEnabled) {
        return;
      }

      try {
        type WindowWithAudioCtx = typeof window & {
          __audioContext?: AudioContext;
        };
        // ✅ Usar contexto compartilhado
        let audioContext = (window as WindowWithAudioCtx).__audioContext;

        if (!audioContext) {
          try {
            audioContext = new (window.AudioContext ||
              // @ts-expect-error webkitAudioContext tá aí por questões de cross-browser compatibilidade, acho
              (window as WindowWithAudioCtx).webkitAudioContext)();
            (window as WindowWithAudioCtx).__audioContext = audioContext;
          } catch (err) {
            console.error("[ChatInterno] ✗ Falha ao criar AudioContext:", err);
            return;
          }
        }

        // ✅ CRUCIAL: Lidar com autoplay policy (requer interação do usuário)
        if (audioContext.state === "suspended") {
          // Usar promise para garantir que resume foi completado
          audioContext
            .resume()
            .then(() => {
              // Reproduzir som APÓS o resume estar completo
              if (audioContext) this._playBips(audioContext);
            })
            .catch((err: Error) => {
              console.warn(
                "[ChatInterno] ⚠ AudioContext ainda suspenso (autoplay policy):",
                err,
              );
              // Tentar mesmo assim em alguns browsers
              if (audioContext) this._playBips(audioContext);
            });
        } else {
          // AudioContext já está running, reproduzir imediatamente
          this._playBips(audioContext);
        }
      } catch (error) {
        console.error("[ChatInterno] ✗ Erro ao reproduzir som:", error);
      }
    },

    /**
     * ✅ NOVO: Função auxiliar para reproduzir os bips
     * Separada para poder ser chamada após o resume estar completo
     */
    _playBips(audioContext: AudioContext) {
      try {
        const now = audioContext.currentTime;

        // ✅ Primeiro bip (800 Hz, 100ms)
        const osc1 = audioContext.createOscillator();
        const gain1 = audioContext.createGain();

        osc1.type = "sine";
        osc1.frequency.value = 800;
        osc1.connect(gain1);
        gain1.connect(audioContext.destination);

        gain1.gain.setValueAtTime(0.3, now);
        gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

        osc1.start(now);
        osc1.stop(now + 0.1);

        // ✅ Segundo bip (1000 Hz, 100ms) após 150ms
        const startTime2 = now + 0.15;

        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();

        osc2.type = "sine";
        osc2.frequency.value = 1000;
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);

        gain2.gain.setValueAtTime(0.3, startTime2);
        gain2.gain.exponentialRampToValueAtTime(0.01, startTime2 + 0.1);

        osc2.start(startTime2);
        osc2.stop(startTime2 + 0.1);
      } catch (error) {
        console.error("[ChatInterno] ✗ Erro ao reproduzir bips:", error);
      }
    },
    /* controlam o estado global da lista de chats e qual está aberto */
    setInternalChatMode(value: boolean) {
      this.isInternalChatMode = value;

      if (!value) {
        this.activeChatId = null;
      }
    },

    /**
     * Anuncia que este operador está digitando no chat ativo. Throttled na função de
     * módulo; aqui só se resolve o destinatário.
     *
     * Ação (não getter) porque toca `this.wsService`: getter chamando action fecha o
     * ciclo de inferência que degrada o `this` deste store inteiro.
     */
    anunciarDigitacaoChatInterno() {
      if (!this.wsService) return;
      notificarDigitacaoChatInterno(
        this.wsService,
        this.activeChatId,
        this.activeChat?.otherUserId,
      );
    },

    /** Anuncia que parou. Chamado ao enviar, ao esvaziar o campo e ao sair do chat. */
    anunciarParadaDigitacaoChatInterno(idChat?: string, idDestinatario?: string) {
      if (!this.wsService) return;
      notificarParouDeDigitarChatInterno(
        this.wsService,
        idChat ?? this.activeChatId,
        idDestinatario ?? this.activeChat?.otherUserId,
      );
    },

    async initWebsocket() {
      const auth = useAuthStoreUser();
      const ws = await auth.initWebsocket();

      this.wsService = ws;
      void this.registerInternoCallbacks();
    },

    async registerInternoCallbacks() {
      const ws = this.wsService;
      if (!ws) return;

      if (!ws.ws.connected) await ws.connect();

      // Evita re-registrar callbacks em chamadas subsequentes (caso contrário cada
      // evento WS seria entregue N vezes).
      if (this.wsInitialized) return;
      this.wsInitialized = true;

      // Client-side blip: a fresh 'conectado' ack after the first means the
      // socket recovered. Server-side blip: socket-server emits RESYNC after its
      // NATS link reconnects. Both cover the same gap, so both resync.
      ws.registerCallback("conectado" as any, async () => {
        // Na primeira conexão this.initialized ainda é false — fetchChats cuida disso.
        await this._resyncAfterReconnect();
      });

      ws.registerCallback("RESYNC" as any, async () => {
        await this._resyncAfterReconnect();
      });

      ws.registerCallback("CHAT_INTERNO.NOVA_MENSAGEM", (_payload) => {
        const mensagem = _payload as unknown as MensagemInterna;
        // Chegou mensagem ⇒ quem digitava terminou. Apaga ANTES de processar,
        // senão o indicador fica pulsando embaixo da mensagem recém-chegada até o
        // TTL de 8 s: o `paused` pode se perder e, mesmo quando vem, corre contra
        // a própria mensagem.
        if (mensagem?.idChat) limparPresencaChatInterno(mensagem.idChat);
        this._onMensagemInternaRecebida(mensagem);
      });

      ws.registerCallback(
        "CHAT_INTERNO.MENSAGEM_EDITADA",
        (_payload: unknown) => {
          const mensagem = _payload as MensagemInterna;
          this._onMensagemInternaEditada(mensagem);
        },
      );

      ws.registerCallback("CHAT_INTERNO.CHAT_CRIADO", (payload: unknown) => {
        const chat = payload as ChatInternoSocketPayload;

        // ✅ Verificar em allChats
        const jaExiste = this.allChats.some((c) => c.id === chat.id);
        if (jaExiste) return;

        // ✅ Mapear para ChatUI (vai ter a foto do outro usuário do payload)
        let chatUI = this.mapChatInternoToUIFromSocket(chat);

        // Se o contato já foi carregado, pegar o isOnline dele
        const auth = useAuthStoreUser();
        const otherUserId =
          chat.idUsuarioA === auth.user?.id
            ? chat.usuarioB?.id
            : chat.usuarioA?.id;

        if (otherUserId) {
          // Procurar em this.contacts (que foi carregado com status)
          const contactComStatus = this.contacts.find(
            (c) => c.id === `contact-${otherUserId}`,
          );

          if (contactComStatus) {
            // ✅ Atualizar foto e status do contato carregado
            chatUI = {
              ...chatUI,
              avatar: contactComStatus.avatar,
              isOnline: contactComStatus.isOnline,
            };
          }
        }

        // ✅ Adicionar em allChats com dados atualizados
        this.allChats.unshift(chatUI);
      });

      ws.registerCallback("CHAT_INTERNO.STATUS_USUARIO", (payload: unknown) => {
        const { idUsuario, status } = payload as {
          idUsuario: string;
          status: "online" | "offline";
        };
        this._onStatusUsuarioChanged(idUsuario, status);
      });

      // ✅ MENSAGEM ENTREGUE
      ws.registerCallback(
        "CHAT_INTERNO.MENSAGEM_ENTREGUE",
        <T extends { idChat?: string }>(payload: T & WebhookPayload) => {
          const { idChat } = payload;
          if (!idChat) return;

          // `this.messages` é SÓ do chat aberto: sem comparar com o chat do evento,
          // uma entrega em outra conversa pintava ✓✓ nas mensagens desta.
          if (idChat === this.activeChatId) {
            this.messages.forEach((m) => {
              if (m.isSent && m.readStatus === "sent") {
                m.readStatus = "delivered";
              }
            });
          }

          // Prévia da sidebar vale para qualquer chat, aberto ou não.
          this._aplicarAckNaSidebar(idChat, "delivered");
        },
      );

      // ✅ MENSAGEM LIDA
      ws.registerCallback(
        "CHAT_INTERNO.MENSAGEM_LIDA",
        <T extends { idChat?: string; idUsuario?: string }>(
          payload: T & WebhookPayload,
        ) => {
          const auth = useAuthStoreUser();
          const meuId = auth.user?.id;

          const { idChat, idUsuario } = payload;
          if (!idChat) return;

          // 🔒 1. Se EU li, ignora (não posso marcar minhas mensagens)
          if (idUsuario === meuId) return;

          // Prévia da sidebar antes do corte por chat ativo: o outro leu, e isso
          // tem que aparecer na lista mesmo com a conversa fechada.
          this._aplicarAckNaSidebar(idChat, "read");

          // 🔒 2. Se não é o chat ativo, ignora
          if (idChat !== this.activeChatId) return;

          // ✅ 3. Agora SIM: o outro usuário leu minhas mensagens
          this.messages.forEach((m) => {
            if (
              m.isSent &&
              (m.readStatus === "sent" || m.readStatus === "delivered")
            ) {
              m.readStatus = "read";
            }
          });

          // Badge zerado (quem leu foi o outro)
          const chat = this.chats.find((c) => c.id === idChat);
          if (chat) {
            chat.unreadCount = 0;
          }
        },
      );

      ws.registerCallback("CHAT_INTERNO.DIGITANDO", (payload: unknown) => {
        const { idChat, idUsuario, presenca } = payload as {
          idChat: string;
          idUsuario: string;
          presenca: PresencaChatInterno;
        };
        aplicarPresencaChatInterno({ idChat, idUsuario, presenca });
      });

      ws.registerCallback("CHAT_INTERNO.REACAO", (payload: unknown) => {
        const { idMensagem, idUsuario, reacao } = payload as {
          idMensagem: string;
          idUsuario: string;
          reacao: string | null;
        };
        const msg = this.messages.find((m) => m.id === idMensagem);
        if (!msg) return;
        if (!msg.reactions) msg.reactions = new Map();
        if (!reacao) {
          msg.reactions.delete(idUsuario);
        } else {
          msg.reactions.set(idUsuario, reacao);
        }
      });
    },

    async fetchContacts() {
      const auth = useAuthStoreUser();
      if (!auth.currentConta?.id) return;

      this.isLoadingContacts = true;

      try {
        const authUserId = auth.user?.id;
        if (!authUserId) {
          this.contacts = [];
          return;
        }

        const usuarios = await chatsInternosService.usuariosContas(
          String(auth.currentConta.id),
        );

        // ✅ Busca status online/offline
        const statusList = await chatsInternosService.usuariosStatus(
          String(auth.currentConta.id),
        );

        const usuariosFiltrados = usuarios.filter(
          (u) => String(u.id) !== String(authUserId),
        );

        this.contacts = usuariosFiltrados.map((u) => {
          const statusInfo = statusList.find((s) => s.id === u.id);
          return {
            ...this.mapUsuarioToChatUI(u),
            isOnline: statusInfo?.isOnline ?? false,
          };
        });
      } catch (e) {
        console.error("Erro ao buscar usuários da conta", e);
        this.contacts = [];
      } finally {
        this.isLoadingContacts = false;
      }
    },

    setActiveChat(chat: ChatUI) {
      const auth = useAuthStoreUser();

      // Saindo de um chat onde este operador estava digitando: avisa antes de trocar,
      // senão o outro lado fica com "escrevendo..." fantasma até o TTL de 8 s. Usa o
      // chat ANTERIOR — depois da atribuição abaixo ele já não existe mais aqui.
      if (this.activeChatId && this.activeChatId !== chat.id) {
        this.anunciarParadaDigitacaoChatInterno(
          this.activeChatId,
          this.activeChat?.otherUserId,
        );
      }

      this.activeChatId = chat.id;
      this.messages = [];
      this.nextCursor = undefined;

      // ✅ Verificar se é um chat com mensagens ou um draft
      const localChat = this.chats.find((c) => c.id === chat.id);
      const isChatWithMessages = !!localChat;

      this.initialUnreadCount = 0;

      if (isChatWithMessages && localChat) {
        // ✅ Chat já está na sidebar (tem mensagens)
        this.activeChatDraft = null; // Limpar draft

        const previousUnreadCount = localChat.unreadCount;
        this.initialUnreadCount = previousUnreadCount;
        localChat.unreadCount = 0; // Remove o badge visualmente

        // ✅ Marcar como lida IMEDIATAMENTE ao abrir um chat específico
        this.markChatAsRead(chat.id);

        if (!auth.currentConta?.id) return;

        // Chamar backend para marcar como lida
        chatsInternosService
          .marcarComoLido(String(auth.currentConta.id), chat.id)
          .catch(() => {
            if (localChat) localChat.unreadCount = previousUnreadCount;
          });
      } else {
        // ✅ Chat é um draft (foi criado mas ainda não tem mensagens)
        this.activeChatDraft = chat;
      }

      void this.loadMessages();
    },

    async createChatWithContact(contactId: string) {
      const auth = useAuthStoreUser();
      if (!auth.currentConta?.id) return;

      const userId = contactId.startsWith("contact-")
        ? contactId.replace("contact-", "")
        : contactId;

      const normalizedUserId = String(userId);

      const chatExistente = this.chats.find(
        (chat) => String(chat.otherUserId) === normalizedUserId,
      );

      if (chatExistente) {
        this.setActiveChat(chatExistente);
        return;
      }

      try {
        // 1️⃣ Criar chat
        const chatCriado = await chatsInternosService.criarChat(
          String(auth.currentConta.id),
          { idUsuario: normalizedUserId },
        );

        // 2️⃣ Mapear chat com dados básicos
        const chatUI = this.mapChatInternoCriadoToUI(chatCriado);

        // 3️⃣ ⭐Buscar status do contato já carregado
        // Se fetchContacts() já foi chamado, o contato está em this.contacts
        const contactCarregado = this.contacts.find(
          (c) => c.id === `contact-${normalizedUserId}`,
        );

        // 4️⃣ Se o contato foi carregado previamente, usar seu isOnline
        // (que já foi sincronizado pelo WebSocket em _onStatusUsuarioChanged)
        if (contactCarregado) {
          chatUI.isOnline = contactCarregado.isOnline ?? false;
        }
        // Se não foi carregado, deixar undefined (será atualizado quando WS enviar STATUS_USUARIO)

        // 5️⃣ ✅ Adicionar em allChats IMEDIATAMENTE (mas NÃO em visibleChats)
        // Será promovido para visibleChats quando primeira mensagem chegar
        if (!this.allChats.some((c) => c.id === chatUI.id)) {
          this.allChats.unshift(chatUI);
        }

        this.setActiveChat(chatUI);
      } catch (error) {
        console.error("Erro ao criar chat interno", error);
        throw error;
      }
    },

    async _loadChatsFromBackend(
      accountId: string,
      mode: "initial" | "reconnect",
    ) {
      const auth = useAuthStoreUser();
      const [chatsResponse, statusList] = await Promise.all([
        chatsInternosService.listarChats(accountId, {
          pagina: 1,
          resultadosPorPagina: 50,
        }),
        chatsInternosService.usuariosStatus(accountId),
      ]);

      const statusById = new Map(statusList.map((s) => [s.id, s]));
      const itemById = new Map(chatsResponse.dados.map((d) => [d.id, d]));
      const authUserId = auth.user?.id;

      this.allChats = chatsResponse.dados.map((item: ChatInternoListItem) => {
        const chatUI = this.mapChatInternoToUIFromList(item);
        const participantId =
          item.idUsuarioA === authUserId ? item.idUsuarioB : item.idUsuarioA;
        const statusInfo = statusById.get(participantId);

        if (mode === "reconnect") {
          const localCount = this.unreadCountByChat[chatUI.id] ?? 0;
          this.unreadCountByChat[chatUI.id] = Math.max(
            localCount,
            chatUI.unreadCount,
          );
        } else if (this.unreadCountByChat[chatUI.id] === undefined) {
          this.unreadCountByChat[chatUI.id] = chatUI.unreadCount;
        }

        return {
          ...chatUI,
          unreadCount: this.unreadCountByChat[chatUI.id] ?? chatUI.unreadCount,
          isOnline: statusInfo?.isOnline ?? false,
        };
      });

      this.visibleChats = this.allChats.filter(
        (chat) => !!itemById.get(chat.id)?.ultimaMensagemConteudo,
      );
    },

    async fetchChats() {
      const auth = useAuthStoreUser();

      // ✅ Se já estiver carregando ou já carregou, não faz nada a menos que seja forçado
      if (this.isLoadingChats || this.initialized) {
        // Se já está inicializado, apenas garante que o WS está OK
        if (this.initialized) await this.initWebsocket();
        return;
      }

      if (!auth.initialized) await auth.init();

      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw) {
        console.warn("[InternalChatStore] accountId ainda não definido");
        return;
      }
      const accountId = String(accountIdRaw);

      this.isLoadingChats = true;

      try {
        await this._loadChatsFromBackend(accountId, "initial");

        // ✅ IMPORTANTE: Chamar initWebsocket SEMPRE
        // Mesmo que o usuário não esteja no chat interno
        // As notificações funcionam em background
        await this.initWebsocket();
      } catch (error) {
        console.error("Erro ao buscar chats internos:", error);
      } finally {
        this.isLoadingChats = false;
        this.initialized = true;
      }
    },

    // Recovery after a websocket blip (client- or server-side): reload the chat
    // list AND the open conversation's thread, covering events missed during the
    // gap. No-op before the store is initialized — the initial load handles that.
    async _resyncAfterReconnect() {
      // Um gap de socket engole eventos, e o `paused` engolido deixaria
      // "escrevendo..." preso na tela até o TTL. Zera antes de recarregar — mesma
      // decisão do `chatStore.resync`. Também limpa o throttle: depois de um gap,
      // a próxima digitação deve anunciar na hora.
      limparTodasPresencasChatInterno();

      if (!this.initialized) return;
      await Promise.all([
        this._syncChatListAfterReconnect(),
        this._refreshActiveChatAfterReconnect(),
      ]);
    },

    async _syncChatListAfterReconnect() {
      const accountIdRaw = useAuthStoreUser().currentConta?.id;
      if (!accountIdRaw) return;

      try {
        await this._loadChatsFromBackend(String(accountIdRaw), "reconnect");
      } catch (error) {
        console.error(
          "[InternalChatStore] Erro ao re-sincronizar chats após reconexão:",
          error,
        );
      }
    },

    /**
     * Após reconexão do socket, recupera o que pode ter sido perdido no chat ativo:
     *  - mensagens enviadas pelo outro usuário durante o gap;
     *  - estados de entrega/leitura das minhas mensagens que ainda estão visíveis.
     * Sem isso, o chat aberto fica desatualizado até o usuário trocar de conversa.
     */
    async _refreshActiveChatAfterReconnect() {
      const accountIdRaw = useAuthStoreUser().currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;
      const accountId = String(accountIdRaw);
      const activeChatId = this.activeChatId;

      try {
        const response = await chatsInternosService.listarMensagens(
          accountId,
          activeChatId,
          { quantidade: 50 },
        );
        const { mensagens: novasMensagens, estadoOutroUsuario } = response;

        const dataUltimaLeituraOutro = estadoOutroUsuario?.ultimaLeitura
          ? new Date(estadoOutroUsuario.ultimaLeitura)
          : null;
        const dataUltimaEntregaOutro = estadoOutroUsuario?.ultimaEntrega
          ? new Date(estadoOutroUsuario.ultimaEntrega)
          : null;

        const applyStatus = (msg: Message, dataEnvio: Date) => {
          if (
            dataUltimaLeituraOutro &&
            dataEnvio <= dataUltimaLeituraOutro &&
            msg.readStatus !== "read"
          ) {
            msg.readStatus = "read";
          } else if (
            dataUltimaEntregaOutro &&
            dataEnvio <= dataUltimaEntregaOutro &&
            msg.readStatus === "sent"
          ) {
            msg.readStatus = "delivered";
          }
        };

        // 1) Re-avalia status das mensagens que já estão em tela (minhas, ainda não "read")
        for (const msg of this.messages) {
          if (!msg.isSent || msg.readStatus === "read") continue;
          applyStatus(msg, new Date(msg.time));
        }

        // 2) Adiciona mensagens novas chegadas durante o gap (eventos NOVA_MENSAGEM perdidos)
        const existentes = new Set(this.messages.map((m) => m.id));
        const novas: Message[] = [];
        let recebeuMensagemDoOutro = false;

        for (const m of novasMensagens) {
          const msgUI = this.mapMensagemInternaToMessageUI(m);
          if (existentes.has(msgUI.id)) continue;

          if (msgUI.isSent && msgUI.readStatus === "sent") {
            applyStatus(msgUI, new Date(m.dataEnvio));
          } else if (!msgUI.isSent) {
            recebeuMensagemDoOutro = true;
          }
          this._markProcessed(m.id);
          novas.push(msgUI);
        }

        if (novas.length > 0) {
          novas.sort(
            (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
          );
          // As novas são mais recentes que as existentes — appendar no fim.
          this.messages = [...this.messages, ...novas];
        }

        // 3) Se chegaram mensagens do outro e o chat está ativo, marcar como lidas.
        if (
          recebeuMensagemDoOutro &&
          this.isInternalChatMode &&
          this.activeChatId === activeChatId
        ) {
          chatsInternosService
            .marcarComoLido(accountId, activeChatId)
            .catch((err) => {
              console.error(
                "[ChatInterno] Erro ao marcar como lido pós-reconnect:",
                err,
              );
            });
          this.markChatAsRead(activeChatId);
          const chat = this.visibleChats.find((c) => c.id === activeChatId);
          if (chat) chat.unreadCount = 0;
        }
      } catch (error) {
        console.error(
          "[InternalChatStore] Erro ao recuperar mensagens do chat ativo após reconexão:",
          error,
        );
      }
    },

    async reloadForConta() {
      // Resetar estado
      this.allChats = [];
      this.contacts = [];
      this.activeChatId = null;
      this.messages = [];
      this.initialized = false; // 🔑 IMPORTANTE: Marcar como não inicializado

      // Recarregar
      await this.fetchChats();
    },

    /*-------------------------------------------------------------------------------------------------- */

    /* Tudo relacionado a mandar, receber ou alterar o conteúdo das mensagens */
    /**
     * ✅ Adiciona um chat à sidebar quando ele recebe sua primeira mensagem
     * Checa se é um draft e promove para chat "real"
     */
    _promoteDraftChatToSidebar(chat: ChatUI) {
      // ✅ Adicionar em allChats se não existir
      if (!this.allChats.some((c) => c.id === chat.id)) {
        this.allChats.unshift(chat);
      }

      // ✅ Promover para visibleChats
      this._promoteToVisibleChats(chat.id);

      // Se era um draft ativo, limpar
      if (this.activeChatDraft?.id === chat.id) {
        this.activeChatDraft = null;
      }
    },
    _promoteToVisibleChats(chatId: string) {
      // Se já está em visibleChats, não faz nada
      if (this.visibleChats.some((c) => c.id === chatId)) {
        return;
      }

      // Procura em allChats
      const chat = this.allChats.find((c) => c.id === chatId);
      if (chat) {
        this.visibleChats.unshift(chat);
      }
    },
    async loadMessages(cursor?: string) {
      this.clearReply();
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;
      const accountId = String(accountIdRaw);

      this.isLoadingMessages = true;

      try {
        const effectiveCursor = cursor ?? this.nextCursor;

        const queryParams: MensagemQueryParams = {
          quantidade: 50,
          ...(effectiveCursor ? { cursor: effectiveCursor } : {}),
        };

        const response = await chatsInternosService.listarMensagens(
          accountId,
          this.activeChatId,
          queryParams,
        );

        const { mensagens: novasMensagens, estadoOutroUsuario } = response;

        if (!novasMensagens.length) return;

        // ✅ Datas vindas DIRETO do backend
        const dataUltimaLeituraOutro = estadoOutroUsuario?.ultimaLeitura
          ? new Date(estadoOutroUsuario.ultimaLeitura)
          : null;

        const dataUltimaEntregaOutro = estadoOutroUsuario?.ultimaEntrega
          ? new Date(estadoOutroUsuario.ultimaEntrega)
          : null;

        const existentes = new Set(this.messages.map((m) => m.id));
        const mensagensMapeadas = novasMensagens.reduce<Message[]>((acc, m) => {
          const msgUI = this.mapMensagemInternaToMessageUI(m);
          if (existentes.has(msgUI.id)) return acc;

          // ✅ Só recalcula status das mensagens ENVIADAS POR MIM
          if (msgUI.isSent && msgUI.readStatus === "sent") {
            const dataEnvio = new Date(m.dataEnvio);

            // 🟦 LIDO
            if (dataUltimaLeituraOutro && dataEnvio <= dataUltimaLeituraOutro) {
              msgUI.readStatus = "read";
            }
            // ⬜ ENTREGUE
            else if (
              dataUltimaEntregaOutro &&
              dataEnvio <= dataUltimaEntregaOutro
            ) {
              msgUI.readStatus = "delivered";
            }
          }

          acc.push(msgUI);
          return acc;
        }, []);

        mensagensMapeadas.sort(
          (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
        );

        this.messages = [...mensagensMapeadas, ...this.messages];

        const ultima = novasMensagens[novasMensagens.length - 1];
        this.nextCursor = ultima.cursor ?? ultima.id;
      } catch (error) {
        console.error("Erro ao carregar mensagens:", error);
      } finally {
        this.isLoadingMessages = false;
      }
    },

    async sendMessage(
      texto: string,
      replyToId?: string,
      extra?: {
        tipo?: "texto" | "audio" | "imagem" | "file";
        url?: string;
        nome?: string;
        tamanho?: number;
        mime?: string;
        duracao?: string;
        waveform?: number[];
      },
    ) {
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;

      const accountId = String(accountIdRaw);
      const finalReplyId = this.messageToReply?.id || replyToId;

      const tempId = crypto.randomUUID();

      // Preview de mídia citada (quando respondendo a uma figurinha/imagem/áudio/arquivo)
      const replyMediaUrl =
        this.messageToReply?.fileData?.url ?? this.messageToReply?.audioUrl;

      // ✅ UI otimista ANTES de enviar
      const optimisticMsg: Message = {
        id: makeIdMensagem(tempId),
        idExterno: tempId,
        tempId,
        time: new Date().toISOString(),
        isSent: true,
        readStatus: "sent", // ✅ Começa como "sent"
        type:
          extra?.tipo === "imagem"
            ? "imagem"
            : extra?.tipo === "audio"
              ? "audio"
              : extra?.tipo === "file"
                ? "file"
                : "text",
        text: texto,
        isBot: false,
        sender: auth.user?.nome,
        replyToId: finalReplyId,
        replyText: this.messageToReply?.text,
        replyOwner: this.messageToReply?.sender,
        replyType: this.messageToReply?.type,
        replyUrl: replyMediaUrl,
      };

      this.messages.push(optimisticMsg);
      this.clearReply();

      try {
        const payload =
          extra?.tipo && extra.tipo !== "texto"
            ? {
                tipo: extra.tipo as
                  | "texto"
                  | "audio"
                  | "imagem"
                  | "file"
                  | "video"
                  | "documento",
                mensagem: texto || undefined,
                url: extra.url,
                nomeArquivo: extra.nome,
                tamanhoArquivo:
                  extra.tamanho != null ? extra.tamanho : undefined,
                mime: extra.mime,
                respostaPara: finalReplyId,
              }
            : {
                tipo: "texto" as const,
                mensagem: texto,
                respostaPara: finalReplyId,
              };

        // ✅ Enviar para API
        const novaMsgApi = await chatsInternosService.enviarMensagem(
          accountId,
          this.activeChatId,
          payload,
        );

        this.initialUnreadCount = 0;

        const novaMsgUI: Message =
          this.mapMensagemInternaToMessageUI(novaMsgApi);
        novaMsgUI.readStatus = "sent"; // ✅ Status padrão é "sent"

        // ✅ Marcar como processada ANTES de atualizar
        this._markProcessed(novaMsgApi.id);

        // ✅ Substituir otimista pela real
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) {
          this.messages[index] = novaMsgUI;
        } else {
          this.messages.push(novaMsgUI);
        }

        // ✅ Adicionar chat à sidebar se for a primeira mensagem
        // ✅ Se é um draft, promover para visibleChats
        if (this.activeChatDraft?.id === this.activeChatId) {
          this._promoteDraftChatToSidebar(this.activeChatDraft);
        } else {
          // ✅ Se já é chat normal, apenas promover se necessário
          const chatUI = this.activeChat;
          if (chatUI) {
            this._promoteToVisibleChats(chatUI.id);
          }
        }

        this._updateSidebarAfterMessage(
          novaMsgUI,
          novaMsgApi.idRemetente,
          novaMsgApi.dataEnvio,
        );

        this.clearReply();
      } catch (error) {
        console.error("Erro ao enviar mensagem interna:", error);
        // Remover otimista em caso de erro
        const idx = this.messages.findIndex((m) => m.tempId === tempId);
        if (idx !== -1) {
          this.messages.splice(idx, 1);
        }
      }
    },

    async sendAudioMessage(url: string) {
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;
      const accountId = String(accountIdRaw);
      try {
        const novaMsgApi = await chatsInternosService.enviarMensagem(
          accountId,
          this.activeChatId,
          {
            tipo: "audio",
            url,
          },
        );

        const novaMsgUI = this.mapMensagemInternaToMessageUI(novaMsgApi);

        // Evita duplicação: só adiciona se não existir
        const foundMsg = this.messages.find((m) => m.id === novaMsgApi.id);
        if (!foundMsg) {
          this.messages.push(novaMsgUI);
        } else {
          Object.assign(foundMsg, novaMsgUI);
        }

        // Atualiza sidebar para mídia
        this._updateSidebarAfterMessage(
          novaMsgUI,
          novaMsgApi.idRemetente,
          novaMsgApi.dataEnvio,
        );
      } catch (error) {
        console.error("Erro ao enviar áudio interno:", error);
      }
    },

    async sendStickerMessage(file: File) {
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;
      const accountId = String(accountIdRaw);

      const tempId = crypto.randomUUID();
      const localUrl = URL.createObjectURL(file);

      const optimisticMsg: Message = {
        id: makeIdMensagem(tempId),
        idExterno: tempId,
        tempId,
        type: "sticker",
        time: new Date().toISOString(),
        isSent: true,
        readStatus: "sent",
        isBot: false,
        sender: auth.user?.nome,
        fileData: {
          url: localUrl,
          mimeType: file.type,
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2) + " MB",
          sizeBytes: file.size,
        },
      };

      this.messages.push(optimisticMsg);

      try {
        const storage = await storageService.uploadChatInternoFile({ file });

        const novaMsgApi = await chatsInternosService.enviarMensagem(
          accountId,
          this.activeChatId,
          {
            tipo: "figurinha",
            url: storage.path,
          },
        );

        this._markProcessed(novaMsgApi.id);

        const mapped = this.mapMensagemInternaToMessageUI(novaMsgApi);
        const stickerMapped: Message = {
          ...mapped,
          type: "sticker",
          subtype: "sticker",
          fileData: mapped.fileData ?? optimisticMsg.fileData,
        };
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) this.messages[index] = stickerMapped;
        else this.messages.push(stickerMapped);

        this._updateSidebarAfterMessage(
          stickerMapped,
          novaMsgApi.idRemetente,
          novaMsgApi.dataEnvio,
        );
        URL.revokeObjectURL(localUrl);
      } catch (err) {
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) {
          this.messages[index].uploadError = true;
        }
        console.error("Erro ao enviar figurinha interna:", err);
      }
    },

    /**
     * Envia uma figurinha que já está hospedada no nosso storage (biblioteca ou
     * recentes) informando apenas a URL. Não faz upload nem baixa os bytes no
     * navegador — é isso que evita o `TypeError: Failed to fetch` do Chrome ao
     * usar figurinhas salvas no Chat Interno.
     */
    async sendStickerMessageByUrl(url: string) {
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId || !url) return;
      const accountId = String(accountIdRaw);

      const tempId = crypto.randomUUID();

      const optimisticMsg: Message = {
        id: makeIdMensagem(tempId),
        idExterno: tempId,
        tempId,
        type: "sticker",
        time: new Date().toISOString(),
        isSent: true,
        readStatus: "sent",
        isBot: false,
        sender: auth.user?.nome,
        fileData: {
          url,
          mimeType: "image/webp",
          name: "sticker.webp",
          size: "",
        },
      };

      this.messages.push(optimisticMsg);

      try {
        const novaMsgApi = await chatsInternosService.enviarMensagem(
          accountId,
          this.activeChatId,
          {
            tipo: "figurinha",
            url,
          },
        );

        this._markProcessed(novaMsgApi.id);

        const mapped = this.mapMensagemInternaToMessageUI(novaMsgApi);
        const stickerMapped: Message = {
          ...mapped,
          type: "sticker",
          subtype: "sticker",
          fileData: mapped.fileData ?? optimisticMsg.fileData,
        };
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) this.messages[index] = stickerMapped;
        else this.messages.push(stickerMapped);

        this._updateSidebarAfterMessage(
          stickerMapped,
          novaMsgApi.idRemetente,
          novaMsgApi.dataEnvio,
        );
      } catch (err) {
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) {
          this.messages[index].uploadError = true;
        }
        console.error("Erro ao enviar figurinha interna:", err);
        throw err;
      }
    },

    async sendFileInternalMessage(file: File, caption?: string) {
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;

      const accountId = String(accountIdRaw);

      // 🔹 ID temporário
      const tempId = crypto.randomUUID();

      // 🔹 Preview local
      const localUrl = URL.createObjectURL(file);

      // 🔹 Tipo
      const isImage = file.type.startsWith("image/");
      const isAudio = file.type.startsWith("audio/");

      let tipo: "documento" | "imagem" | "audio" = "documento";
      if (isImage) tipo = "imagem";
      if (isAudio) tipo = "audio";

      let tipoFake: "file" | "imagem" | "audio" = "file";
      if (isImage) tipoFake = "imagem";
      if (isAudio) tipoFake = "audio";

      // 1️⃣ UI otimista (TIPADA CORRETAMENTE)
      const optimisticMessage: Message = {
        id: makeIdMensagem(tempId),
        idExterno: tempId, // 👈 obrigatório
        tempId,

        type: tipoFake,
        time: new Date().toISOString(),
        isSent: true,

        readStatus: "sent", // 👈 Alterado de 'sent' para 'pending'
        isBot: false, // 👈 obrigatório
        sender: auth.user?.nome,

        isUploading: true,

        fileData: {
          url: localUrl, // preview local
          mimeType: file.type,
          caption,
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2) + " MB",
          sizeBytes: file.size,
        },
      };

      this.messages.push(optimisticMessage);

      try {
        // 2️⃣ Upload real
        const storage = await storageService.uploadChatInternoFile(
          { file },
          isAudio,
        );

        // 3️⃣ Envio real
        const novaMsgApi = await chatsInternosService.enviarMensagem(
          accountId,
          this.activeChatId,
          {
            tipo,
            legenda: caption || undefined,
            url: storage.path,
            nomeArquivo: file.name,
            mime: file.type,
            tamanhoArquivo: file.size,
          },
        );

        // 4️⃣ Map real
        const mapped = this.mapMensagemInternaToMessageUI(novaMsgApi);

        // 5️⃣ Substitui fake → real
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) {
          this.messages[index] = {
            ...mapped,
            isUploading: false,
          };
        }

        // 6️⃣ Sidebar
        this._updateSidebarAfterMessage(
          mapped,
          novaMsgApi.idRemetente,
          novaMsgApi.dataEnvio,
        );

        URL.revokeObjectURL(localUrl);
      } catch (err) {
        // ❌ Falha
        const index = this.messages.findIndex((m) => m.tempId === tempId);
        if (index !== -1) {
          this.messages[index].uploadError = true;
          this.messages[index].isUploading = false;
        }

        console.error("Erro ao enviar arquivo interno:", err);
      }
    },

    async editarMensagem(idMensagem: string, texto: string) {
      const auth = useAuthStoreUser();
      const accountIdRaw = auth.currentConta?.id;
      if (!accountIdRaw || !this.activeChatId) return;
      const accountId = String(accountIdRaw);

      try {
        await chatsInternosService.editarMensagem(
          accountId,
          this.activeChatId,
          idMensagem,
          { conteudo: texto },
        );

        // 1️⃣ Atualiza a mensagem na lista do chat
        const msg = this.messages.find((m) => m.id === idMensagem);
        if (msg) msg.text = texto;

        // 2️⃣ Atualiza a última mensagem na sidebar
        const chat = this.chats.find((c) => c.id === this.activeChatId);
        if (chat) {
          chat.lastMessage.text = texto; // só atualiza o texto
          chat.lastMessage.sender = msg?.sender ?? chat.lastMessage.sender;
          chat.lastMessage.type = msg?.type ?? chat.lastMessage.type;

          chat.time = msg?.time ?? chat.time;

          // 3️⃣ Move chat pro topo se quiser
          const index = this.chats.findIndex((c) => c.id === chat.id);
          if (index > 0) {
            this.chats.splice(index, 1);
            this.chats.unshift(chat);
          }
        }

        // 4️⃣ Limpa estado de edição
        this.clearEditMessage();
      } catch (error) {
        console.error("Erro ao editar mensagem:", error);
      }
    },

    setDraft(chatId: string, text: string | null) {
      if (!chatId) return;

      if (text && text.trim().length > 0) {
        this.draftsDict[chatId] = text;
      } else {
        delete this.draftsDict[chatId];
      }
    },

    setEditMessage(message: Message) {
      this.messageToReply = null;
      this.messageToEdit = message;
    },

    clearEditMessage() {
      this.messageToEdit = null;
    },

    setReplyMessage(msg: Message) {
      this.messageToReply = msg;
    },

    clearReply() {
      this.messageToReply = null;
    },

    /*-------------------------------------------------------------------------------------------------- */

    /* Funcionalidade de Encaminhamento */

    startForwarding(message: Message) {
      this.isForwarding = true;
      this.selectionContext = "forward";
      // Um balão de álbum (`media_grid`) tem ID sintético: resolve nas mensagens reais.
      this.forwardingMessageIds = resolveSelectableMessageIds(message);
    },

    startMassForwarding() {
      this.isForwarding = true;
      this.selectionContext = "forward";
      this.forwardingMessageIds = [];
    },

    startSelectionMode() {
      this.isForwarding = true;
      this.selectionContext = "manage";
      this.forwardingMessageIds = [];
    },

    /**
     * Alterna a seleção de uma ou várias mensagens de uma vez. Um balão de álbum
     * (`media_grid`) representa N mídias, então o toggle é atômico: ou o grupo inteiro
     * entra na seleção, ou o grupo inteiro sai dela.
     */
    toggleForwardMessage(target: string | string[]) {
      const ids = Array.isArray(target) ? target : [target];
      if (ids.length === 0) return;

      const allSelected = ids.every((id) =>
        this.forwardingMessageIds.includes(id),
      );

      if (allSelected) {
        this.forwardingMessageIds = this.forwardingMessageIds.filter(
          (id) => !ids.includes(id),
        );
        if (
          this.forwardingMessageIds.length === 0 &&
          this.selectionContext === "forward"
        ) {
          this.cancelForwarding();
        }
      } else {
        for (const id of ids) {
          if (!this.forwardingMessageIds.includes(id))
            this.forwardingMessageIds.push(id);
        }
      }
    },

    async commitForwarding(
      chatsToForwardTo: ChatUI[],
      opcoes: OpcoesEncaminhamento = {},
    ) {
      const accountIdRaw = useAuthStoreUser().currentConta?.id;
      if (!accountIdRaw) return;
      const accountId = String(accountIdRaw);

      const messagesToForward = this.messages.filter((m) =>
        this.forwardingMessageIds.includes(m.id),
      );
      // Sem isso um ID que não resolve para mensagem real falha em silêncio.
      if (messagesToForward.length === 0) {
        toast.error("Nenhuma mensagem selecionada para encaminhar.");
        return;
      }

      const buildPayload = (originalMsg: Message) => {
        if (originalMsg.type === "text") {
          return {
            tipo: "texto" as const,
            mensagem: originalMsg.text,
          };
        }
        if (
          originalMsg.type === "imagem" ||
          originalMsg.type === "file" ||
          originalMsg.type === "audio"
        ) {
          const tipoEnvio =
            originalMsg.type === "file" ? "documento" : originalMsg.type;
          return {
            tipo: tipoEnvio as
              | "texto"
              | "audio"
              | "imagem"
              | "file"
              | "video"
              | "documento",
            url: originalMsg.fileData?.url || originalMsg.audioUrl,
            nomeArquivo: originalMsg.fileData?.name,
            tamanhoArquivo: originalMsg.fileData?.sizeBytes,
            mime: originalMsg.fileData?.mimeType,
            legenda: legendaParaEncaminhar(originalMsg, opcoes),
          };
        }
        return null;
      };

      // Resolve cada chat-alvo em paralelo (cria se for "contact-"), depois dispara todos os envios em paralelo.
      const targetChatIds = await Promise.all(
        chatsToForwardTo.map(async (targetChat) => {
          if (!targetChat.id.startsWith("contact-")) return targetChat.id;
          try {
            const newChat = await chatsInternosService.criarChat(accountId, {
              idUsuario: targetChat.id.replace("contact-", ""),
            });
            return newChat.id;
          } catch (e) {
            console.error("Erro ao criar chat para encaminhar", e);
            return null;
          }
        }),
      );

      // Para cada alvo: envia as mensagens em paralelo e, ao final, atualiza o
      // próprio sidebar com a última mensagem enviada (sem isso, o remetente
      // não vê a conversa subir na lista — só o destinatário recebe via socket).
      const perTargetResults = await Promise.all(
        targetChatIds.map(async (targetChatId) => {
          if (!targetChatId)
            return { targetChatId: null, sent: 0, failed: 0 } as const;

          const sends = messagesToForward
            .map((m) => buildPayload(m))
            .filter((p): p is NonNullable<typeof p> => p !== null)
            .map((payload) =>
              chatsInternosService.enviarMensagem(
                accountId,
                targetChatId,
                payload,
              ),
            );

          const results = await Promise.allSettled(sends);
          const successes = results.flatMap((r) =>
            r.status === "fulfilled" ? [r.value] : [],
          );
          const failed = results.length - successes.length;

          if (successes.length > 0) {
            this._updateForwardedTargetSidebar(targetChatId, successes);
          }
          return { targetChatId, sent: successes.length, failed } as const;
        }),
      );

      const failed = perTargetResults.reduce((acc, r) => acc + r.failed, 0);
      const total = perTargetResults.reduce(
        (acc, r) => acc + r.sent + r.failed,
        0,
      );
      if (failed > 0) {
        console.error(
          `[chat-interno] ${failed}/${total} envios de encaminhamento falharam`,
        );
      }
    },

    cancelForwarding() {
      this.isForwarding = false;
      this.forwardingMessageIds = [];
      this.selectionContext = null;
    },

    /*-------------------------------------------------------------------------------------------------- */

    /* Ações menores que o usuário faz em mensagens ou chats (declaradas apenas para evitar erros) */
    /**
     * O chat interno não tem exclusão de mensagem — não existe rota no backend
     * (`mensagens-chat-interno.controller.ts` só expõe criar e reagir). Retorna `false`
     * para quem chama não anunciar sucesso de algo que não aconteceu.
     */
    deleteMessage(): Promise<boolean> {
      return Promise.resolve(false);
    },
    toggleReaction(message: Message, emoji: string) {
      return this.reactToMessage(message, emoji);
    },
    async reactToMessage(message: Message, emoji: string) {
      const auth = useAuthStoreUser();
      const accountId = String(auth.currentConta?.id);
      const userId = auth.user?.id || "me";

      if (!accountId || !this.activeChatId) return;

      // Optimistic update antes do await para evitar race condition com o websocket
      if (!message.reactions) message.reactions = new Map();
      const existingReaction = message.reactions.get(userId);
      if (existingReaction === emoji) {
        message.reactions.delete(userId);
      } else {
        message.reactions.set(userId, emoji);
      }

      try {
        await chatsInternosService.enviarReacao(
          accountId,
          this.activeChatId,
          message.id,
          emoji,
        );
      } catch (e) {
        // Rollback: desfaz o optimistic update
        if (existingReaction) {
          message.reactions.set(userId, existingReaction);
        } else {
          message.reactions.delete(userId);
        }
        console.error("[chat-interno] erro ao enviar reação:", e);
        toast.error("Não foi possível enviar a reação.");
      }
    },
    toggleFavoriteMessage() {
      return;
    },
    unpinMessage() {
      return;
    },
    toggleMute() {
      return;
    },
    toggleFavorite() {
      return;
    },

    /*-------------------------------------------------------------------------------------------------- */

    /*Funções chamadas reativamente (ex: via socket) */
    _onMensagemInternaRecebida(mensagem: MensagemInterna) {
      const auth = useAuthStoreUser();
      const messageUI = this.mapMensagemInternaToMessageUI(mensagem);
      const chatId = mensagem.idChat;

      let chat = this.getChatById(chatId);

      // ✅ PASSO 3: Se nem na sidebar nem é draft, ignorar
      if (!chat) {
        console.warn(
          "[ChatInterno] Chat não encontrado para mensagem:",
          chatId,
        );
        return;
      }

      // ✅ DEDUPLICAÇÃO IMEDIATA
      if (this.processedMessageIds.has(mensagem.id)) return;

      // ✅ Marcar como processada IMEDIATAMENTE
      this._markProcessed(mensagem.id);

      const meuId = auth.user?.id;
      const ehMinhaMensagem = String(mensagem.idRemetente) === String(meuId);

      // ================================================================
      // CASO 1: É MINHA MENSAGEM (enviada por mim, recebida via socket)
      // ================================================================
      if (ehMinhaMensagem) {
        return; // Ignorar (vem via socket de entrega/leitura)
      }

      // ================================================================
      // CASO 2: MENSAGEM RECEBIDA DE OUTRO USUÁRIO
      // ================================================================

      // ✅ IMPORTANTE: Se é um draft, promover para sidebar ANTES de processar
      // ✅ IMPORTANTE: Sempre promover para visibleChats se não estiver
      const eraInvisivel = !this.visibleChats.find((c) => c.id === chat!.id);
      if (eraInvisivel) {
        // Se é um draft, promover completamente
        if (this.activeChatDraft?.id === chat.id) {
          this._promoteDraftChatToSidebar(chat);
        } else {
          // Se já está em allChats, apenas promover visibilidade
          this._promoteToVisibleChats(chat.id);
        }

        // 🔑 Pegar referência atualizada (agora está em visibleChats)
        chat = this.visibleChats.find((c) => c.id === chatId)!;
      }

      // SCENARIO A: Chat está INATIVO (usuário não está vendo este chat)
      if (!this.isInternalChatMode || this.activeChatId !== chatId) {
        // ✅ Incrementar unread
        this.incrementUnreadCount(chatId);
        chat.unreadCount++;

        // ✅ Marcar como ENTREGUE no backend (não lido ainda)
        if (auth.currentConta?.id) {
          chatsInternosService
            .marcarComoEntregue(String(auth.currentConta.id), chatId)
            .catch((err) => {
              console.error("[ChatInterno] Erro ao marcar como entregue:", err);
            });
        }

        // ✅ Tocar som de notificação
        this.playNotificationSound();
      }
      // SCENARIO B: Chat está ATIVO (usuário está vendo este chat)
      else {
        // ✅ Adicionar mensagem à tela
        const idx = this.messages.findIndex((m) => m.id === mensagem.id);
        if (idx < 0) {
          this.messages.push(messageUI);
        } else {
          this.messages.splice(idx, 1, messageUI);
        }

        // ✅ Marcar como LIDO no backend (chat ativo = usuário viu)
        if (auth.currentConta?.id) {
          chatsInternosService
            .marcarComoLido(String(auth.currentConta.id), chatId)
            .catch((err) => {
              console.error("[ChatInterno] Erro ao marcar como lido:", err);
            });
        }

        // ✅ Zerar unread
        chat.unreadCount = 0;
        this.markChatAsRead(chatId);
      }

      // ================================================================
      // ATUALIZAR SIDEBAR (válido para ambos os casos)
      // ================================================================
      // ✅ DEPOIS - Envolvemos tudo em um if(chat)
      if (chat) {
        const computedText = this.computeLastMessageText(messageUI);
        chat.lastMessage = {
          text: computedText,
          sender: mensagem.idRemetente,
          type: messageUI.type,
          ogType: mensagem.tipo
            ? mensagem.tipo.toUpperCase()
            : this._ogTypeFromUIType(messageUI.type),
          isFromMe: false,
          status: null,
        };

        chat.time = mensagem.dataEnvio;

        // Mover para o topo
        const index = this.chats.findIndex((c) => c.id === chat!.id);
        if (index > 0) {
          const moved = this.chats.splice(index, 1)[0];
          this.chats.unshift(moved);
        }
      }
    },

    _onMensagemInternaEditada(mensagem: MensagemInterna) {
      const idx = this.messages.findIndex((m) => m.id === mensagem.id);
      if (idx === -1) return;

      const messageUI = this.mapMensagemInternaToMessageUI(mensagem);

      // preserva estado local
      this.messages.splice(idx, 1, {
        ...this.messages[idx],
        ...messageUI,
        isEdited: true,
      });

      // Atualiza sidebar se for a última mensagem
      const chat = this.chats.find((c) => c.id === mensagem.idChat);
      if (!chat) return;

      if (chat.lastMessage?.text === this.messages[idx]?.text) {
        chat.lastMessage.text = messageUI.text || "";
      }
    },

    /**
     * Ack da prévia na sidebar. Só mexe quando a última mensagem é MINHA — a do
     * outro não tem ack — e nunca regride (`entregue` chegando depois de `lida`,
     * por reordenação de eventos, não apaga o ✓✓ azul).
     *
     * Percorre `allChats` e `visibleChats`: são arrays distintos e o mesmo chat
     * pode estar como instância separada em cada um.
     */
    _aplicarAckNaSidebar(idChat: string, ack: "delivered" | "read") {
      const precedencia: Record<string, number> = {
        sent: 0,
        delivered: 1,
        read: 2,
      };

      for (const lista of [this.allChats, this.visibleChats]) {
        for (const chat of lista) {
          if (chat.id !== idChat) continue;
          if (!chat.lastMessage?.isFromMe) continue;

          const atual = chat.lastMessage.status ?? "sent";
          if ((precedencia[atual] ?? 0) >= precedencia[ack]) continue;

          chat.lastMessage.status = ack;
        }
      }
    },

    _onStatusUsuarioChanged(idUsuario: string, status: "online" | "offline") {
      // Atualiza na lista de contatos (tem prefixo 'contact-')
      const contact = this.contacts.find(
        (c) => c.id === `contact-${idUsuario}`,
      );
      if (contact) {
        contact.isOnline = status === "online";
      }

      // Atualiza na lista de chats (se existir)
      const chat = this.chats.find((c) => String(c.otherUserId) === idUsuario);
      if (chat) {
        chat.isOnline = status === "online";
      }
    },

    /*-------------------------------------------------------------------------------------------------- */

    /* Funções puras de transformação de dados e formatação */
    // Normaliza strings de tipo (vindas da API/lista) para o tipo usado na UI
    _mapTipoToUIType(t?: string | null): Message["type"] {
      if (!t) return "text";
      const tt = t.toLowerCase();
      if (tt.includes("figurinha") || tt.includes("sticker")) return "sticker";
      if (tt.includes("audio")) return "audio";
      if (tt.includes("imagem") || tt.includes("image")) return "imagem";
      if (
        tt.includes("documento") ||
        tt.includes("document") ||
        tt.includes("file") ||
        tt.includes("video")
      )
        return "file";
      return "text";
    },

    _ogTypeFromUIType(t: Message["type"]): string {
      switch (t) {
        case "sticker":
          return "FIGURINHA";
        case "imagem":
          return "IMAGEM";
        case "audio":
          return "AUDIO";
        case "file":
          return "DOCUMENTO";
        default:
          return "TEXTO";
      }
    },

    mapMensagemInternaToMessageUI(m: MensagemInterna): Message {
      const authStore = useAuthStoreUser();
      const userId = authStore.user?.id;

      const tipoLower = m.tipo ? m.tipo.toLowerCase() : "texto";
      const isSticker = tipoLower === "figurinha";
      const isImage = tipoLower === "imagem";
      const isFile =
        tipoLower === "file" ||
        tipoLower === "documento" ||
        isImage ||
        isSticker;
      const isAudio = tipoLower === "audio";

      const replyMessageFake: Message | undefined = m.dadosResposta
        ? ({
            id: m.dadosResposta.id,
            text: m.dadosResposta.conteudo,
            isSent: m.dadosResposta.idRemetente === String(userId),
            isBot: m.dadosResposta.tipoRemetente === "bot",
          } as Message)
        : undefined;

      // ✅ LÓGICA DE STATUS:
      // Se fui EU que enviei:
      //   - Status padrão é "sent"
      //   - Será atualizado por socket ou em loadMessages se necessário
      // Se foi OUTRO usuário:
      //   - Se m.lida === true → status = "read"
      //   - Senão → status = "sent"
      const readStatus =
        userId && m.idRemetente === userId
          ? "sent" // ✅ MINHAS mensagens começam como "sent"
          : m.lida
            ? "read" // ✅ Mensagens RECEBIDAS lidas
            : "sent"; // ✅ Mensagens RECEBIDAS não lidas

      return {
        id: makeIdMensagem(m.id),
        idExterno: m.id,
        time: m.dataEnvio,
        isSent: userId ? m.idRemetente === userId : false,
        sender: undefined,
        readStatus,

        type: isSticker
          ? "sticker"
          : isImage
            ? "imagem"
            : isFile
              ? "file"
              : isAudio
                ? "audio"
                : "text",
        subtype: isSticker ? "sticker" : undefined,
        text: !isImage ? (m.mensagem ?? m.legenda ?? undefined) : undefined,
        isBot: false,
        isEdited: !!m.editado,
        isDeleted: false,
        reactions: m.reacoes
          ? new Map(Object.entries(m.reacoes))
          : new Map<string, string>(),

        fileData:
          (isFile || isImage) && m.url
            ? {
                url: m.url,
                name: m.nomeArquivo ?? "Imagem",
                size: m.tamanhoArquivo
                  ? this.formatFileSize(m.tamanhoArquivo)
                  : "—",
                sizeBytes: m.tamanhoArquivo ?? undefined,
                mimeType: m.mime || this.guessMimeType(m.nomeArquivo),
                caption: m.legenda ?? undefined,
              }
            : undefined,

        audioUrl: isAudio ? m.url : undefined,
        replyToId: m.dadosResposta?.id,
        replyText: m.dadosResposta?.conteudo ?? undefined,
        replyType: m.dadosResposta?.tipo
          ? this._mapTipoToUIType(m.dadosResposta.tipo)
          : undefined,
        replyUrl: m.dadosResposta?.url ?? undefined,
        replyOwner: replyMessageFake
          ? this.getMessageOwnerName(replyMessageFake)
          : undefined,
      };
    },

    mapChatInternoToUIFromList(item: ChatInternoListItem): ChatUI {
      const authStore = useAuthStoreUser();
      const userId = authStore.user?.id;

      // ✅ Determinar qual é o outro usuário
      const isUserA = userId && item.usuarioA?.id === userId;
      const outroUsuario = isUserA ? item.usuarioB : item.usuarioA;

      const chatName = outroUsuario?.nome || "Chat Interno";
      const otherUserId = outroUsuario?.id;

      // ✅ PEGAR A FOTO DO OUTRO USUÁRIO
      const avatarUrl = outroUsuario?.foto || null;

      // Monta um messageUI minimal a partir das infos da lista, quando possível
      const typeFromList = this._mapTipoToUIType(item.ultimaMensagemTipo);
      const minimalMessageUI: Message = {
        id: makeIdMensagem("preview"),
        idExterno: "preview",
        time: item.dataUltimaMensagem || new Date().toISOString(),
        // `String()` nos dois lados: o resto do arquivo já faz isso (ver
        // `dadosResposta` em `mapMensagemInternaToMessageUI`) porque o id do
        // token e o id vindo da lista não têm garantia de mesmo tipo. Um
        // `===` cru errando aqui apaga o ack inteiro, não só o ✓✓.
        isSent: userId
          ? String(item.ultimaMensagemEnviadorId) === String(userId)
          : false,
        sender: undefined,
        readStatus: "sent",
        type: typeFromList,
        text:
          typeFromList === "text"
            ? item.ultimaMensagemConteudo || ""
            : undefined,
        isBot: false,
        isEdited: false,
        isDeleted: false,
        reactions: new Map<string, string>(),
        fileData: undefined,
        audioUrl: undefined,
        replyToId: undefined,
        replyText: undefined,
        replyOwner: undefined,
      };

      const lastTextComputed = this.computeLastMessageText(minimalMessageUI);

      return {
        id: item.id,
        name: chatName,
        avatar: avatarUrl, // ✅ AQUI: usar a foto do outro usuário
        time:
          item.dataUltimaMensagem &&
          !isNaN(new Date(item.dataUltimaMensagem).getTime())
            ? item.dataUltimaMensagem
            : new Date().toISOString(),
        lastMessage: {
          text: lastTextComputed || "",
          sender: item.ultimaMensagemEnviadorId || undefined,
          type: minimalMessageUI.type,
          ogType: item.ultimaMensagemTipo
            ? item.ultimaMensagemTipo.toUpperCase()
            : "TEXTO", // ✅ GARANTIR UPPERCASE
          isFromMe: minimalMessageUI.isSent,
          status: ackUltimaMensagemInterna({
            enviadaPorMim: minimalMessageUI.isSent,
            dataUltimaMensagem: item.dataUltimaMensagem,
            estados: item.estados,
            meuId: userId,
          }),
        },
        unreadCount: item.unreadCount || 0,
        isPinned: false,
        isMuted: false,
        isFavorite: false,
        tags: [],
        atendentes: [],
        status: "Online",
        otherUserId,
        estados: item.estados || [],
      };
    },

    mapChatInternoToUIFromSocket(chat: ChatInternoSocketPayload): ChatUI {
      const authStore = useAuthStoreUser();
      const userId = authStore.user?.id;

      const otherUser =
        chat.idUsuarioA === userId ? chat.usuarioB : chat.usuarioA;

      return {
        id: chat.id,
        name: otherUser?.nome || "Chat Interno",
        avatar: null,
        time: chat.dataUltimaMensagem ?? new Date().toISOString(),
        lastMessage: {
          text: chat.ultimaMensagemConteudo ?? "",
          sender: chat.ultimaMensagemEnviadorId ?? undefined,
          type: chat.ultimaMensagemTipo ?? "text",
          ogType: chat.ultimaMensagemTipo
            ? chat.ultimaMensagemTipo.toUpperCase()
            : "TEXTO", // ✅ GARANTIR UPPERCASE
          isFromMe:
            !!userId &&
            String(chat.ultimaMensagemEnviadorId) === String(userId),
          status: ackUltimaMensagemInterna({
            enviadaPorMim:
              !!userId &&
              String(chat.ultimaMensagemEnviadorId) === String(userId),
            dataUltimaMensagem: chat.dataUltimaMensagem,
            estados: chat.estados,
            meuId: userId,
          }),
        },
        unreadCount: 0,
        isPinned: false,
        isMuted: false,
        isFavorite: false,
        tags: [],
        atendentes: [],
        status: "Online",
        otherUserId: otherUser?.id,
        estados: chat.estados || [],
      };
    },

    mapChatInternoCriadoToUI(chat: ChatInterno): ChatUI {
      const authStore = useAuthStoreUser();
      const myUserId = authStore.user?.id;

      const otherUser =
        chat.idUsuarioA === myUserId ? chat.usuarioB : chat.usuarioA;

      return {
        id: chat.id,
        name: otherUser?.nome ?? "Chat Interno",
        avatar: otherUser?.foto ?? null,

        time: new Date().toISOString(),

        lastMessage: {
          text: "",
          sender: undefined,
          type: "text",
          ogType: "TEXTO",
        },

        unreadCount: 0,

        isPinned: false,
        isMuted: false,
        isFavorite: false,

        tags: [],
        atendentes: [
          { idUsuario: chat.idUsuarioA },
          { idUsuario: chat.idUsuarioB },
        ],

        status: "Online",
        otherUserId: otherUser?.id,
        estados: chat.estados || [],
      };
    },

    mapUsuarioToChatUI(usuario: UsuarioConta): ChatUI {
      return {
        id: `contact-${usuario.id}`,
        name: usuario.nome,
        avatar: usuario.foto ?? null,
        time: new Date(usuario.dataCriacao).toISOString(),

        lastMessage: {
          text: "Iniciar conversa",
          sender: undefined,
          type: "text",
          ogType: "TEXTO",
        },

        unreadCount: 0,
        isPinned: false,
        isMuted: false,
        isFavorite: false,
        tags: [],
        atendentes: [],
        status: usuario.ativo ? "Online" : "Offline",
      };
    },

    computeLastMessageText(messageUI: Message): string {
      // Para texto, retornamos o texto diretamente para evitar depender da estrutura específica do serviço
      if (messageUI.type === "text") {
        return messageUI.text || "";
      }

      // Mapeamento de tipos para o serviço de mensagens
      let tipo = "TEXTO" as
        | "TEXTO"
        | "AUDIO"
        | "FIGURINHA"
        | "IMAGEM"
        | "DOCUMENTO";
      let content: { tipo: typeof tipo; texto?: string; url?: string } = {
        tipo: "TEXTO",
        texto: messageUI.text || "",
      };

      if (messageUI.type === "sticker") {
        tipo = "FIGURINHA";
        content = { tipo: "FIGURINHA", url: messageUI.fileData?.url || "" };
      } else if (messageUI.type === "audio") {
        tipo = "AUDIO";
        content = { tipo: "AUDIO", url: messageUI.audioUrl || "" };
      } else if (messageUI.type === "imagem") {
        if (messageUI.subtype === "sticker") {
          tipo = "FIGURINHA";
          content = { tipo: "FIGURINHA", url: messageUI.fileData?.url || "" };
        } else {
          tipo = "IMAGEM";
          content = { tipo: "IMAGEM", url: messageUI.fileData?.url || "" };
        }
      } else if (messageUI.type === "file") {
        tipo = "DOCUMENTO";
        content = { tipo: "DOCUMENTO", url: messageUI.fileData?.url || "" };
      }

      return getDisplayTextForMessageContent(content, tipo) || "";
    },

    _updateSidebarAfterMessage(
      messageUI: Message,
      senderId?: string,
      apiTime?: string,
    ) {
      // ✅ Procurar em visibleChats (onde a UI renderiza)
      const chatIndex = this.visibleChats.findIndex(
        (c) => c.id === this.activeChatId,
      );

      if (chatIndex !== -1) {
        const computedText = this.computeLastMessageText(messageUI);

        this.visibleChats[chatIndex].lastMessage = {
          text: computedText,
          sender: senderId,
          type: messageUI.type,
          ogType: this._ogTypeFromUIType(messageUI.type),
          isFromMe: messageUI.isSent,
          status: messageUI.readStatus ?? "sent",
        };

        this.visibleChats[chatIndex].time =
          apiTime && !isNaN(new Date(apiTime).getTime())
            ? apiTime
            : new Date().toISOString();

        this.visibleChats[chatIndex].unreadCount = 0;

        const chat = this.visibleChats.splice(chatIndex, 1)[0];
        this.visibleChats.unshift(chat);
      }
    },

    /**
     * Atualiza a sidebar de um chat-alvo de encaminhamento usando a última
     * mensagem enviada. Promove para `visibleChats` se ainda estiver só em
     * `allChats` (chat sem mensagens).
     */
    _updateForwardedTargetSidebar(
      targetChatId: string,
      sentMessages: MensagemInterna[],
    ) {
      if (sentMessages.length === 0) return;
      const last = sentMessages[sentMessages.length - 1];
      const messageUI = this.mapMensagemInternaToMessageUI(last);

      // Garante que o chat aparece na sidebar (caso seja um chat recém-criado pelo forward)
      this._promoteToVisibleChats(targetChatId);

      const idx = this.visibleChats.findIndex((c) => c.id === targetChatId);
      if (idx === -1) return;

      const chat = this.visibleChats[idx];
      chat.lastMessage = {
        text: this.computeLastMessageText(messageUI),
        sender: last.idRemetente,
        type: messageUI.type,
        ogType: this._ogTypeFromUIType(messageUI.type),
        isFromMe: messageUI.isSent,
        status: messageUI.readStatus ?? "sent",
      };
      chat.time = last.dataEnvio;

      if (idx > 0) {
        const [moved] = this.visibleChats.splice(idx, 1);
        this.visibleChats.unshift(moved);
      }
    },

    formatFileSize(bytes: number): string {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    guessMimeType(fileName?: string): string {
      if (!fileName) return "application/octet-stream";

      const ext = fileName.split(".").pop()?.toLowerCase();

      switch (ext) {
        case "jpg":
        case "jpeg":
          return "image/jpeg";
        case "png":
          return "image/png";
        case "webp":
          return "image/webp";
        case "gif":
          return "image/gif";
        case "mp3":
          return "audio/mpeg";
        case "ogg":
          return "audio/ogg";
        case "pdf":
          return "application/pdf";
        case "mp4":
          return "video/mp4";
        case "webm":
          return "video/webm";
        case "mov":
          return "video/quicktime";
        case "avi":
          return "video/x-msvideo";
        case "mkv":
          return "video/x-matroska";
        default:
          return "application/octet-stream";
      }
    },

    getMessageOwnerName(message: Message): string {
      if (message.isSent) {
        return "Você";
      }
      if (message.isBot) {
        return "Bot";
      }
      // Se for recebida, pega o nome do contato ativo
      return this.activeChat?.name || "Contato";
    },

    ifReplyGetProperData() {
      if (!this.messageToReply) {
        return {
          replyToId: undefined,
          replyText: undefined,
          replyOwner: undefined,
        };
      }

      return {
        replyToId: this.messageToReply.id,
        replyText:
          this.messageToReply.text ||
          (this.messageToReply.type === "audio" ? "Áudio" : "Mensagem"),
        replyOwner: this.getMessageOwnerName(this.messageToReply),
      };
    },
    limpar() {
      this.allChats = [];
      this.visibleChats = [];
      this.contacts = [];
      this.activeChatId = null;
      this.messages = [];
      this.messageToEdit = null;
      this.messageToReply = null;
      this.isLoadingChats = false;
      this.isLoadingMessages = false;
      this.nextCursor = undefined;
      this.initialized = false;
      this.wsInitialized = false;
      this.isLoadingContacts = false;
      this.draftsDict = {};
      this.isInternalChatMode = false;
      this.processedMessageIds = new Set();
      this.chatsComMensagensNavas = new Set();
      this.unreadCountByChat = {};
      this.isForwarding = false;
      this.forwardingMessageIds = [];
      this.selectionContext = null;
      this.wsService = null;
      this.initialUnreadCount = 0;
      this.activeChatDraft = null;
    },
    /*-------------------------------------------------------------------------------------------------- */
  },
});
